import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, LabelList,
  PieChart, Pie,
  LineChart, Line, Brush,
} from 'recharts';
import {
  Download, ArrowUpDown, Percent,
  Table2, BarChart3, PieChart as PieIcon, TrendingUp,
} from 'lucide-react';

// ─── Constants ───────────────────────────────────────────────────────────────

const CHART_COLORS = [
  '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6',
  '#ef4444', '#06b6d4', '#d97706', '#6366f1',
  '#f97316', '#14b8a6',
];

const VIEW_CONFIG = {
  table:  { label: 'Table',      icon: Table2 },
  bar:    { label: 'Bar Chart',  icon: BarChart3 },
  pie:    { label: 'Pie Chart',  icon: PieIcon },
  line:   { label: 'Line Chart', icon: TrendingUp },
};

// ─── Utilities ───────────────────────────────────────────────────────────────

function formatValue(val) {
  if (val == null) return '';
  const num = Number(val);
  if (isNaN(num)) return String(val);
  if (Math.abs(num) >= 1e6) return (num / 1e6).toFixed(1) + 'M';
  if (Math.abs(num) >= 1e3) return (num / 1e3).toFixed(1) + 'K';
  if (Number.isInteger(num)) return num.toLocaleString();
  return num.toFixed(2);
}

function truncate(str, max = 18) {
  const s = String(str);
  return s.length > max ? s.slice(0, max - 1) + '\u2026' : s;
}

// ─── Data shape detection ────────────────────────────────────────────────────

function isDateColumn(colName, values) {
  const name = colName.toLowerCase();
  const dateKw = ['date', 'time', 'month', 'year', 'week', 'quarter', 'day', 'period'];
  if (dateKw.some(kw => name.includes(kw))) return true;
  const nonNull = values.filter(v => v != null && v !== '');
  if (nonNull.length === 0) return false;
  const dateRe = /^\d{4}-\d{2}|^\d{1,2}\/\d{1,2}\/|^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i;
  return nonNull.filter(v => dateRe.test(String(v))).length / nonNull.length > 0.5;
}

function isNumericColumn(values) {
  const nonNull = values.filter(v => v != null && v !== '');
  if (nonNull.length === 0) return false;
  return nonNull.filter(v => !isNaN(Number(v))).length / nonNull.length > 0.8;
}

function analyzeData(columns, rows) {
  const categoricalCols = [];
  const numericCols = [];
  const dateCols = [];

  for (const col of columns) {
    const vals = rows.map(r => r[col]);
    if (isDateColumn(col, vals)) dateCols.push(col);
    else if (isNumericColumn(vals)) numericCols.push(col);
    else categoricalCols.push(col);
  }

  let bestChart = 'table';
  if (dateCols.length >= 1 && numericCols.length >= 1) {
    bestChart = 'line';
  } else if (categoricalCols.length >= 1 && numericCols.length >= 1 && numericCols.length <= 3) {
    bestChart = (numericCols.length === 1 && rows.length < 8) ? 'pie' : 'bar';
  }

  const available = ['table'];
  if (categoricalCols.length >= 1 && numericCols.length >= 1) {
    available.push('bar');
    if (rows.length < 8) available.push('pie');
  }
  if (dateCols.length >= 1 && numericCols.length >= 1) available.push('line');

  return { categoricalCols, numericCols, dateCols, bestChart, available };
}

// ─── Custom tooltip ──────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label, totals, showPercent }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg border border-gray-700 max-w-[260px]">
      <p className="font-semibold mb-1 text-gray-200 truncate">{label}</p>
      {payload.filter(p => p.value !== undefined).map((entry, i) => {
        const pct = totals?.[entry.dataKey]
          ? ((entry.value / totals[entry.dataKey]) * 100).toFixed(1)
          : null;
        return (
          <p key={i} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
            <span className="truncate">{entry.name}: {showPercent ? `${Number(entry.value).toFixed(1)}%` : formatValue(entry.value)}</span>
            {!showPercent && pct && <span className="text-gray-400 flex-shrink-0">({pct}%)</span>}
          </p>
        );
      })}
    </div>
  );
}

function PieTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;
  const { name, value } = payload[0];
  const pct = total ? ((value / total) * 100).toFixed(1) : 0;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg">
      <p className="font-semibold">{name}</p>
      <p>{formatValue(value)} ({pct}%)</p>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function SageDataTable({ data }) {
  if (!data || !data.rows || data.rows.length === 0) return null;

  const { columns, rows, total_rows } = data;

  const analysis = useMemo(() => analyzeData(columns, rows), [columns, rows]);
  const { categoricalCols, numericCols, dateCols, bestChart, available } = analysis;

  const [activeView, setActiveView] = useState(bestChart);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const [topN, setTopN] = useState(10);
  const [showPercent, setShowPercent] = useState(false);
  const [hiddenSeries, setHiddenSeries] = useState(new Set());
  const chartRef = useRef(null);

  // Reset when view changes
  useEffect(() => setSelectedCategory(null), [activeView]);

  const categoryCol = categoricalCols[0] || dateCols[0];
  const dateCol = dateCols[0];

  // Totals (from ALL rows, before filtering)
  const totals = useMemo(() => {
    const t = {};
    numericCols.forEach(col => {
      t[col] = rows.reduce((sum, r) => sum + (Number(r[col]) || 0), 0);
    });
    return t;
  }, [rows, numericCols]);

  // Prepared chart data
  const chartData = useMemo(() => {
    let d = rows.map(r => {
      const entry = { ...r };
      numericCols.forEach(col => { entry[col] = Number(entry[col]) || 0; });
      return entry;
    });

    if (activeView === 'bar' || activeView === 'pie') {
      const sortCol = numericCols[0];
      if (sortCol) d.sort((a, b) => sortOrder === 'desc' ? b[sortCol] - a[sortCol] : a[sortCol] - b[sortCol]);
    }

    if (activeView === 'bar' && topN !== 'all' && d.length > topN) {
      d = d.slice(0, topN);
    }

    if (showPercent && (activeView === 'bar')) {
      d = d.map(entry => {
        const p = { ...entry };
        numericCols.forEach(col => {
          p[col] = totals[col] ? (entry[col] / totals[col]) * 100 : 0;
        });
        return p;
      });
    }

    return d;
  }, [rows, numericCols, activeView, sortOrder, topN, showPercent, totals]);

  // Filter table rows when category selected
  const tableRows = useMemo(() => {
    if (!selectedCategory || !categoryCol) return rows;
    return rows.filter(r => String(r[categoryCol]) === selectedCategory);
  }, [rows, selectedCategory, categoryCol]);

  const handleLegendClick = (e) => {
    const key = e.dataKey || e.value;
    setHiddenSeries(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleBarClick = (entry) => {
    if (!entry || !categoryCol) return;
    const cat = String(entry[categoryCol]);
    setSelectedCategory(prev => prev === cat ? null : cat);
  };

  const handlePieClick = (entry) => {
    if (!entry || !categoryCol) return;
    const d = entry.payload || entry;
    const cat = String(d[categoryCol] || d.name);
    setSelectedCategory(prev => prev === cat ? null : cat);
  };

  const handleExport = async () => {
    if (!chartRef.current) return;
    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(chartRef.current, { backgroundColor: '#fff', scale: 2 });
    const link = document.createElement('a');
    link.download = `sage-chart-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const isChart = activeView !== 'table';

  return (
    <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">

      {/* ── View tabs ── */}
      {available.length > 1 && (
        <div className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-b border-gray-200 overflow-x-auto">
          {available.map(v => {
            const { label, icon: Icon } = VIEW_CONFIG[v];
            const on = activeView === v;
            return (
              <button
                key={v}
                onClick={() => setActiveView(v)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium whitespace-nowrap transition-colors
                  ${on ? 'bg-amber-100 text-amber-800 border border-amber-300' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
              >
                <Icon className="w-3 h-3" />
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Chart controls ── */}
      {isChart && (
        <div className="flex items-center gap-2 px-2 py-1.5 bg-white border-b border-gray-100 flex-wrap">
          {activeView === 'bar' && (
            <button
              onClick={() => setSortOrder(p => p === 'desc' ? 'asc' : 'desc')}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-gray-500 hover:bg-gray-100 transition-colors"
            >
              <ArrowUpDown className="w-3 h-3" />
              {sortOrder === 'desc' ? '\u2193 High\u2013Low' : '\u2191 Low\u2013High'}
            </button>
          )}

          {activeView === 'bar' && rows.length > 5 && (
            <select
              value={topN}
              onChange={e => setTopN(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="text-xs text-gray-500 border border-gray-200 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
            >
              <option value={5}>Top 5</option>
              <option value={10}>Top 10</option>
              <option value={15}>Top 15</option>
              <option value="all">All</option>
            </select>
          )}

          {(activeView === 'bar' || activeView === 'pie') && (
            <button
              onClick={() => setShowPercent(p => !p)}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs transition-colors
                ${showPercent ? 'bg-amber-100 text-amber-800' : 'text-gray-500 hover:bg-gray-100'}`}
            >
              <Percent className="w-3 h-3" />
              {showPercent ? '% On' : '% Off'}
            </button>
          )}

          <button
            onClick={handleExport}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs text-gray-500 hover:bg-gray-100 transition-colors ml-auto"
            title="Export as PNG"
          >
            <Download className="w-3 h-3" />
            PNG
          </button>
        </div>
      )}

      {/* ── Chart area ── */}
      {isChart && (
        <div ref={chartRef} className="px-1 py-3 bg-white">

          {/* Bar chart */}
          {activeView === 'bar' && (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 5, right: 15, left: 5, bottom: chartData.length > 6 ? 70 : 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  dataKey={categoryCol}
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  tickFormatter={v => truncate(v, 14)}
                  interval={0}
                  angle={chartData.length > 6 ? -45 : 0}
                  textAnchor={chartData.length > 6 ? 'end' : 'middle'}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#6b7280' }}
                  tickFormatter={v => showPercent ? `${v.toFixed(0)}%` : formatValue(v)}
                  width={55}
                />
                <Tooltip content={<ChartTooltip totals={showPercent ? null : totals} showPercent={showPercent} />} />
                {numericCols.length > 1 && (
                  <Legend onClick={handleLegendClick} wrapperStyle={{ fontSize: 11, cursor: 'pointer' }} />
                )}
                {numericCols.map((col, ci) =>
                  !hiddenSeries.has(col) && (
                    <Bar key={col} dataKey={col} fill={CHART_COLORS[ci % CHART_COLORS.length]}
                         animationDuration={500} onClick={handleBarClick} cursor="pointer">
                      {chartData.length <= 10 && numericCols.length === 1 && (
                        <LabelList dataKey={col} position="top"
                          formatter={v => showPercent ? `${Number(v).toFixed(1)}%` : formatValue(v)}
                          style={{ fontSize: 9, fill: '#6b7280' }} />
                      )}
                      {chartData.map((entry, i) => (
                        <Cell key={i} fill={CHART_COLORS[ci % CHART_COLORS.length]}
                          opacity={selectedCategory == null || String(entry[categoryCol]) === selectedCategory ? 1 : 0.25} />
                      ))}
                    </Bar>
                  )
                )}
              </BarChart>
            </ResponsiveContainer>
          )}

          {/* Pie chart */}
          {activeView === 'pie' && (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey={numericCols[0]}
                  nameKey={categoryCol}
                  cx="50%" cy="45%"
                  outerRadius={90} innerRadius={30}
                  animationDuration={500}
                  onClick={handlePieClick}
                  cursor="pointer"
                >
                  {chartData.map((entry, i) => (
                    <Cell key={i}
                      fill={CHART_COLORS[i % CHART_COLORS.length]}
                      opacity={selectedCategory == null || String(entry[categoryCol]) === selectedCategory ? 1 : 0.25}
                      stroke={String(entry[categoryCol]) === selectedCategory ? '#fff' : 'transparent'}
                      strokeWidth={String(entry[categoryCol]) === selectedCategory ? 2 : 0} />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip total={totals[numericCols[0]]} />} />
                <Legend
                  layout="horizontal" align="center" verticalAlign="bottom"
                  iconSize={8}
                  formatter={v => truncate(v, 20)}
                  onClick={e => {
                    const cat = e.value;
                    setSelectedCategory(prev => prev === cat ? null : cat);
                  }}
                  wrapperStyle={{ fontSize: 10, cursor: 'pointer', lineHeight: '18px', paddingTop: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          )}

          {/* Line chart */}
          {activeView === 'line' && (
            <ResponsiveContainer width="100%" height={numericCols.length > 2 ? 340 : 300}>
              <LineChart data={chartData} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey={dateCol} tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => truncate(v, 10)} />
                <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={formatValue} width={55} />
                <Tooltip content={<ChartTooltip totals={totals} showPercent={false} />} />
                {numericCols.length > 1 && (
                  <Legend onClick={handleLegendClick} iconSize={8}
                    formatter={v => truncate(v, 16)}
                    wrapperStyle={{ fontSize: 10, cursor: 'pointer', lineHeight: '18px' }} />
                )}
                {numericCols.map((col, ci) =>
                  !hiddenSeries.has(col) && (
                    <Line key={col} type="monotone" dataKey={col}
                      stroke={CHART_COLORS[ci % CHART_COLORS.length]} strokeWidth={2}
                      dot={{ r: 3, fill: CHART_COLORS[ci % CHART_COLORS.length] }}
                      activeDot={{ r: 5 }} animationDuration={500} />
                  )
                )}
                {chartData.length > 10 && (
                  <Brush dataKey={dateCol} height={30} stroke="#f59e0b" fill="#fffbeb" travellerWidth={10} />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}

          {/* Selected indicator */}
          {selectedCategory && (
            <div className="flex items-center justify-center gap-2 mt-2 text-xs text-gray-500">
              <span>Filtered: <strong className="text-amber-700">{selectedCategory}</strong></span>
              <button onClick={() => setSelectedCategory(null)}
                className="text-gray-400 hover:text-gray-600 underline">
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Data table ── */}
      <div className={`overflow-x-auto ${isChart ? 'max-h-48 border-t border-gray-200' : 'max-h-64'}`}>
        <table className="w-full text-xs">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              {columns.map(col => (
                <th key={col} className="px-2.5 py-1.5 text-left font-semibold text-gray-600 whitespace-nowrap border-b border-gray-200">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, i) => (
              <tr key={i} className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                ${selectedCategory && String(row[categoryCol]) === selectedCategory ? '!bg-amber-50' : ''}`}>
                {columns.map(col => (
                  <td key={col} className="px-2.5 py-1.5 text-gray-700 whitespace-nowrap border-b border-gray-100">
                    {row[col] != null ? String(row[col]) : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {(total_rows > rows.length || selectedCategory) && (
        <div className="px-2.5 py-1.5 text-xs text-gray-400 bg-gray-50 border-t border-gray-200 text-center">
          Showing {tableRows.length} of {total_rows.toLocaleString()} rows
          {selectedCategory && ` (filtered by "${selectedCategory}")`}
        </div>
      )}
    </div>
  );
}
